import React, { useState, useRef, useEffect } from "react";
import { FiChevronDown, FiX, FiSearch } from "react-icons/fi";
import styles from "../css/shared.module.css";

const UserAutocomplete = ({ 
  users, 
  value, 
  onChange, 
  placeholder = "Buscar usuario...",
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const wrapperRef = useRef(null);

  // Filtrar usuarios basado en el término de búsqueda
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users); // Mostrar todos los usuarios cuando no hay búsqueda
      return;
    }

    const filtered = users.filter(user => 
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 10); // Solo limitar a 10 cuando hay búsqueda activa

    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  // Establecer usuario seleccionado cuando cambia el value
  useEffect(() => {
    if (value) {
      const user = users.find(u => u._id === value);
      setSelectedUser(user);
      setSearchTerm(user ? `${user.username} - ${user.email}` : "");
    } else {
      setSelectedUser(null);
      setSearchTerm("");
    }
  }, [value, users]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);
    
    if (!value.trim()) {
      onChange("");
      setSelectedUser(null);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSearchTerm(`${user.username} - ${user.email}`);
    onChange(user._id);
    setIsOpen(false);
  };

  const handleClear = () => {
    setSearchTerm("");
    setSelectedUser(null);
    onChange("");
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm("");
      }
    }
  };

  return (
    <div className={styles.autocompleteWrapper} ref={wrapperRef}>
      <div className={styles.autocompleteInput}>
        <div className={styles.searchIcon}>
          <FiSearch size={16} />
        </div>
        <input
          type="text"
          className={styles.input}
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          disabled={disabled}
        />
        {selectedUser && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClear}
            disabled={disabled}
          >
            <FiX size={16} />
          </button>
        )}
        <button
          type="button"
          className={`${styles.dropdownButton} ${isOpen ? styles.rotated : ''}`}
          onClick={handleToggle}
          disabled={disabled}
        >
          <FiChevronDown size={16} />
        </button>
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
              <div
                key={user._id}
                className={`${styles.dropdownItem} ${
                  selectedUser?._id === user._id ? styles.selected : ''
                }`}
                onClick={() => handleUserSelect(user)}
              >
                <div className={styles.userInfo}>
                  <span className={styles.username}>{user.username}</span>
                  <span className={styles.email}>{user.email}</span>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.noResults}>
              {searchTerm.trim() ? 'No se encontraron usuarios' : 'Escribe para buscar usuarios...'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserAutocomplete;
